import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@components/dialog";
import { Button } from "@components/button";

/**
 * Tracks whether the builder has edits that haven't finished saving yet.
 *
 * Saves are debounced autosaves (no Save button), so "unsaved" means the window
 * between an edit and its mutation resolving. Each editing field registers a
 * stable key via markDirty(key) when it changes and markClean(key) once its save
 * resolves; isDirty is true while any key is outstanding.
 *
 * Two guards are wired off that flag:
 *  - a native `beforeunload` prompt for real tab close / refresh / external nav
 *    (the browser only allows its own dialog there, not a custom modal), and
 *  - a custom confirm modal for in-app navigation, by intercepting the router's
 *    navigator.push/replace so any <Link>/navigate() is caught while dirty.
 */
type UnsavedChangesContextType = {
  isDirty: boolean;
  markDirty: (key: string) => void;
  markClean: (key: string) => void;
};

const noop = () => undefined;

const UnsavedChangesContext = createContext<UnsavedChangesContextType>({
  isDirty: false,
  markDirty: noop,
  markClean: noop,
});

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const dirtyKeys = useRef<Set<string>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  // Read the latest dirty state inside the patched navigator without re-patching.
  const isDirtyRef = useRef(false);
  // The intercepted navigation to replay if the user confirms leaving.
  const [pendingNav, setPendingNav] = useState<(() => void) | null>(null);

  const syncDirty = useCallback(() => {
    const dirty = dirtyKeys.current.size > 0;
    isDirtyRef.current = dirty;
    setIsDirty(dirty);
  }, []);

  const markDirty = useCallback(
    (key: string) => {
      dirtyKeys.current.add(key);
      syncDirty();
    },
    [syncDirty]
  );

  const markClean = useCallback(
    (key: string) => {
      dirtyKeys.current.delete(key);
      syncDirty();
    },
    [syncDirty]
  );

  // Native browser confirmation on real tab close / refresh / external nav.
  // A custom modal is not possible here — the browser owns this dialog.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Intercept in-app navigation: while dirty, capture the navigation and show the
  // confirm modal instead of leaving. Restored on unmount.
  useEffect(() => {
    const nav = navigator as unknown as {
      push: (...args: unknown[]) => void;
      replace: (...args: unknown[]) => void;
    };
    const originalPush = nav.push.bind(nav);
    const originalReplace = nav.replace.bind(nav);

    nav.push = (...args: unknown[]) => {
      if (isDirtyRef.current) {
        setPendingNav(() => () => originalPush(...args));
      } else {
        originalPush(...args);
      }
    };
    nav.replace = (...args: unknown[]) => {
      if (isDirtyRef.current) {
        setPendingNav(() => () => originalReplace(...args));
      } else {
        originalReplace(...args);
      }
    };

    return () => {
      nav.push = originalPush;
      nav.replace = originalReplace;
    };
  }, [navigator]);

  const confirmLeave = useCallback(() => {
    const go = pendingNav;
    setPendingNav(null);
    // The user accepted losing the unsaved edits; clear so we don't re-prompt.
    dirtyKeys.current.clear();
    syncDirty();
    go?.();
  }, [pendingNav, syncDirty]);

  const cancelLeave = useCallback(() => setPendingNav(null), []);

  return (
    <UnsavedChangesContext.Provider value={{ isDirty, markDirty, markClean }}>
      {children}

      <Dialog
        open={pendingNav !== null}
        onOpenChange={(open) => {
          if (!open) cancelLeave();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>
              Your latest edits are still saving. If you leave now they may not
              be saved. Stay on the page a moment to let them finish.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={cancelLeave}>
              Stay on page
            </Button>
            <Button variant="destructive" onClick={confirmLeave}>
              Leave anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
};
